import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, Users, Shield, MessageSquare } from "lucide-react";

export default function LandingPage() {
    return (
        <div className="min-h-screen noise-bg" data-testid="landing-page">
            <header className="sticky top-0 z-30 glass-panel">
                <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
                    <div className="flex items-center gap-3">
                        <div className="grid h-9 w-9 place-items-center rounded-lg bg-rotary-blue text-white">
                            <Users className="h-4 w-4" />
                        </div>
                        <div>
                            <p className="label-eyebrow leading-none">Directory Platform</p>
                            <p className="rotary-heading text-lg leading-tight text-rotary-ink">Member Directory</p>
                        </div>
                    </div>
                    <Link to="/admin/login">
                        <Button
                            variant="outline"
                            className="border-rotary-blue text-rotary-blue hover:bg-rotary-blue hover:text-white"
                            data-testid="landing-admin-login-btn"
                        >
                            Admin Login <ArrowRight className="ml-1.5 h-4 w-4" />
                        </Button>
                    </Link>
                </div>
            </header>

            <main className="mx-auto max-w-6xl px-6 py-16 sm:py-24">
                <div className="grid gap-16 lg:grid-cols-2 lg:items-center">
                    <div className="space-y-8 animate-fade-up">
                        <span className="label-eyebrow">For associations &amp; clubs</span>
                        <h1 className="rotary-heading text-4xl leading-[0.95] sm:text-5xl lg:text-6xl text-rotary-ink">
                            One directory.
                            <br />
                            <span className="text-rotary-blue">Every officer.</span>
                            <br />
                            <span className="relative inline-block">
                                Always current.
                                <span className="absolute left-0 -bottom-2 h-2 w-full bg-rotary-gold/70" />
                            </span>
                        </h1>
                        <p className="max-w-lg text-base leading-relaxed text-muted-foreground">
                            A private roster tool for associations. Members update their own details through
                            a personalised link. Admins keep the master data clean, and send reminders
                            with a single click.
                        </p>
                        <div className="flex flex-wrap gap-3">
                            <Link to="/admin/login">
                                <Button
                                    className="bg-rotary-blue text-white hover:bg-rotary-sky rounded-full px-6"
                                    data-testid="landing-cta-admin-btn"
                                >
                                    Open Admin Panel <ArrowRight className="ml-1.5 h-4 w-4" />
                                </Button>
                            </Link>
                        </div>
                    </div>

                    <div className="grid gap-4 sm:grid-cols-2">
                        <FeatureCard
                            icon={<Users className="h-5 w-5" />}
                            title="Self-serve profiles"
                            body="Members open a unique link and update their info in under two minutes."
                        />
                        <FeatureCard
                            icon={<MessageSquare className="h-5 w-5" />}
                            title="One-tap reminders"
                            body="Send a personalised WhatsApp to any member who hasn't submitted yet."
                        />
                        <FeatureCard
                            icon={<Shield className="h-5 w-5" />}
                            title="Role-based access"
                            body="Super admin, district admin, and read-only past-year admin — each scoped to their remit."
                        />
                        <FeatureCard
                            gold
                            title="Google Sheet synced"
                            body="Every submission writes back to your master sheet, kept in one canonical place."
                        />
                    </div>
                </div>
            </main>

            <footer className="border-t border-border">
                <div className="mx-auto max-w-6xl px-6 py-6 text-xs text-muted-foreground">
                    Member Directory · Privacy-first roster management for associations.
                </div>
            </footer>
        </div>
    );
}

function FeatureCard({ icon, title, body, gold }) {
    return (
        <div
            className={`rounded-xl border p-6 hover:-translate-y-1 hover:shadow-md ${
                gold ? "bg-rotary-blue text-white border-transparent" : "bg-white"
            }`}
            style={{ transition: "transform 220ms ease, box-shadow 220ms ease" }}
        >
            {icon && (
                <div
                    className={`mb-4 inline-flex h-9 w-9 items-center justify-center rounded-full ${
                        gold ? "bg-rotary-gold text-rotary-ink" : "bg-rotary-blue/10 text-rotary-blue"
                    }`}
                >
                    {icon}
                </div>
            )}
            <p
                className={`rotary-heading text-lg ${gold ? "text-rotary-gold" : "text-rotary-ink"}`}
            >
                {title}
            </p>
            <p
                className={`mt-1 text-sm leading-relaxed ${
                    gold ? "text-white/85" : "text-muted-foreground"
                }`}
            >
                {body}
            </p>
        </div>
    );
}
